import React from 'react';
import { CheckCircle, AlertTriangle, AlertCircle, Info } from 'lucide-react';

export default function ErroresTab({ detalles }) {
  const sinDatos   = detalles.filter(d => !d.tiene_datos);
  const sinCedula  = detalles.filter(d => d.tiene_datos && !d.rnc_cedula?.trim());
  const porPagar   = detalles.filter(d => d.tiene_datos && parseFloat(d.monto_pagar) > 0);

  const hayProblemas = sinDatos.length > 0 || sinCedula.length > 0;

  if (!hayProblemas && porPagar.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <CheckCircle size={40} className="text-green-500 mb-3" />
        <p className="text-sm font-semibold text-gray-700">Sin inconsistencias</p>
        <p className="text-xs text-gray-400 mt-1">Todos los empleados tienen datos completos y cédula registrada.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Sin registros de nómina */}
      {sinDatos.length > 0 && (
        <IssueBlock
          icon={AlertTriangle}
          iconClass="text-red-600"
          bgClass="bg-red-50 border-red-200"
          title={`${sinDatos.length} empleado${sinDatos.length !== 1 ? 's' : ''} sin registros de nómina`}
          description="Estos empleados están en el sistema pero no tienen nóminas cerradas en el año fiscal seleccionado."
          action="Verifica que sus nóminas hayan sido procesadas y cerradas."
          rows={sinDatos}
        />
      )}

      {/* Sin cédula */}
      {sinCedula.length > 0 && (
        <IssueBlock
          icon={AlertCircle}
          iconClass="text-orange-600"
          bgClass="bg-orange-50 border-orange-200"
          title={`${sinCedula.length} empleado${sinCedula.length !== 1 ? 's' : ''} sin cédula/RNC`}
          description="El campo RNC/Cédula está vacío. La DGII requiere este dato para el formulario IR-13."
          action="Actualiza el perfil del empleado con su cédula o RNC."
          rows={sinCedula}
        />
      )}

      {/* Con monto a pagar (informativo) */}
      {porPagar.length > 0 && (
        <IssueBlock
          icon={Info}
          iconClass="text-blue-600"
          bgClass="bg-blue-50 border-blue-200"
          title={`${porPagar.length} empleado${porPagar.length !== 1 ? 's' : ''} con ISR por pagar al fisco`}
          description="El ISR liquidado supera lo retenido en nómina. Esto no bloquea el cierre pero debe reportarse a la DGII."
          action="Verifica con el departamento contable antes de cerrar la declaración."
          rows={porPagar}
          isInfo
        />
      )}

    </div>
  );
}

function IssueBlock({ icon: Icon, iconClass, bgClass, title, description, action, rows, isInfo }) {
  const fmt = (n) => parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });

  return (
    <div className={`border rounded-xl overflow-hidden ${bgClass}`}>
      <div className="flex items-start gap-3 px-4 py-3 border-b border-current border-opacity-20">
        <Icon size={16} className={`mt-0.5 flex-shrink-0 ${iconClass}`} />
        <div>
          <p className="text-sm font-semibold text-gray-800">{title}</p>
          <p className="text-xs text-gray-600 mt-0.5">{description}</p>
          <p className="text-xs font-medium text-gray-700 mt-1">→ {action}</p>
        </div>
      </div>
      <div className="divide-y divide-white divide-opacity-50">
        {rows.map((d, i) => (
          <div key={d.employee_id || i} className="flex items-center justify-between px-4 py-2.5">
            <div>
              <p className="text-sm font-medium text-gray-800">{d.apellidos_nombres}</p>
              <p className="text-xs text-gray-500">{d.rnc_cedula || 'Sin cédula'}</p>
            </div>
            {isInfo && (
              <span className="text-xs font-semibold text-red-700">
                Por pagar: RD$ {fmt(d.monto_pagar)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
